import { useEffect, useMemo, useState } from "react";
import {
  listPartnerLeads,
  getPartnerLead,
  updatePartnerLeadStatus,
  addPartnerLeadNote,
} from "../../api/partnerPortalApi";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";

const STATUS_LABELS = {
  new: "New",
  accepted: "Accepted",
  contacted: "Contacted",
  appointment_set: "Appointment set",
  working: "Working",
  closed_won: "Closed won",
  closed_lost: "Closed lost",
  rejected: "Rejected",
};

const STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "accepted", label: "Accepted" },
  { value: "working", label: "Working" },
  { value: "closed_won", label: "Closed won" },
  { value: "closed_lost", label: "Closed lost" },
];

function leadDisplayName(lead) {
  return [lead.first_name, lead.last_name].filter(Boolean).join(" ") || "Unnamed lead";
}

const LEAD_TYPE_LABELS = {
  dealer_partnership: "Dealer partnership",
  dealer_plan_upgrade: "Plan upgrade request",
  dealer_boost_request: "Boost request",
};

function leadTypeLabel(leadType) {
  return LEAD_TYPE_LABELS[leadType] || leadType || "Lead";
}

function leadMeta(lead) {
  if (!lead.metadata_json || typeof lead.metadata_json !== "object") return null;
  if (
    lead.lead_type === "dealer_partnership" ||
    lead.lead_type === "dealer_plan_upgrade" ||
    lead.lead_type === "dealer_boost_request"
  ) {
    return lead.metadata_json;
  }
  return null;
}

function formatLeadDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function statusTone(status) {
  switch (status) {
    case "new":
      return "blue";
    case "accepted":
    case "contacted":
    case "appointment_set":
      return "amber";
    case "working":
      return "violet";
    case "closed_won":
      return "green";
    case "closed_lost":
    case "rejected":
      return "slate";
    default:
      return "slate";
  }
}

function LeadStatusBadge({ status }) {
  return (
    <span className={`partner-lead-badge partner-lead-badge--${statusTone(status)}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function LeadListItem({ lead, selected, onSelect }) {
  const meta = leadMeta(lead);
  const city = meta?.dealerCity || meta?.dealer_city;

  return (
    <button
      type="button"
      className={`partner-lead-item${selected ? " is-selected" : ""}`}
      onClick={() => onSelect(lead.id)}
    >
      <div className="partner-lead-item__top">
        <span className="partner-lead-item__number">{lead.lead_number}</span>
        <LeadStatusBadge status={lead.partner_status} />
      </div>
      <strong className="partner-lead-item__name">{leadDisplayName(lead)}</strong>
      <p className="partner-lead-item__meta">
        {leadTypeLabel(lead.lead_type)}
        {city ? ` · ${city}` : ""}
      </p>
      {lead.phone ? <span className="partner-lead-item__phone">{lead.phone}</span> : null}
      {formatLeadDate(lead.lead_created_at) ? (
        <span className="partner-lead-item__date">{formatLeadDate(lead.lead_created_at)}</span>
      ) : null}
    </button>
  );
}

function LeadDetailEmpty() {
  return (
    <div className="partner-lead-detail partner-lead-detail--empty">
      <span className="partner-lead-detail__empty-icon" aria-hidden="true">
        ◈
      </span>
      <h3>Select a lead</h3>
      <p className="muted">Choose an assigned lead to review contact details, update status, and add notes.</p>
    </div>
  );
}

function LeadDetailPanel({ detail, note, onNoteChange, onSubmitNote, onStatusChange, saving }) {
  const partnership = detail.dealerPartnership;
  const planUpgrade = detail.dealerPlanUpgrade;
  const boostRequest = detail.dealerBoostRequest;
  const whatsappHref = detail.phone
    ? `https://wa.me/${String(detail.phone).replace(/\D/g, "")}`
    : null;

  const subtitle = planUpgrade
    ? "Dealer plan upgrade request"
    : boostRequest
      ? "Dealer boost request"
      : detail.leadType === "dealer_partnership"
        ? "Dealer partnership outreach"
        : leadTypeLabel(detail.leadType);

  return (
    <div className="partner-lead-detail">
      <header className="partner-lead-detail__header">
        <div>
          <p className="partner-lead-detail__eyebrow">{detail.leadNumber}</p>
          <h2>{detail.contactName || "Lead detail"}</h2>
          <p className="partner-lead-detail__subtitle">{subtitle}</p>
        </div>
        <LeadStatusBadge status={detail.partnerStatus} />
      </header>

      <div className="partner-lead-detail__actions">
        <button
          type="button"
          className="partner-dashboard-btn"
          disabled={saving}
          onClick={() => onStatusChange("contacted")}
        >
          Mark contacted
        </button>
        <button
          type="button"
          className="partner-dashboard-btn partner-dashboard-btn--primary"
          disabled={saving}
          onClick={() => onStatusChange("accepted")}
        >
          Accept lead
        </button>
        <button
          type="button"
          className="partner-dashboard-btn"
          disabled={saving}
          onClick={() => onStatusChange("working")}
        >
          Mark working
        </button>
        <button
          type="button"
          className="partner-dashboard-btn"
          disabled={saving}
          onClick={() => onStatusChange("closed_won")}
        >
          Closed won
        </button>
      </div>

      <div className="partner-lead-detail__grid">
        <section className="partner-lead-detail__card">
          <h3>Contact</h3>
          {detail.contactName ? <p>{detail.contactName}</p> : null}
          {detail.phone ? (
            <p>
              WhatsApp:{" "}
              {whatsappHref ? (
                <a href={whatsappHref} target="_blank" rel="noreferrer">
                  {detail.phone}
                </a>
              ) : (
                detail.phone
              )}
            </p>
          ) : null}
          {detail.email ? <p>{detail.email}</p> : null}
          {detail.clientViewName ? <p className="muted">{detail.clientViewName}</p> : null}
        </section>

        {partnership ? (
          <section className="partner-lead-detail__card">
            <h3>Dealer opportunity</h3>
            <p>{partnership.dealerDisplayName || detail.contactName}</p>
            {partnership.dealerCity ? <p>{partnership.dealerCity}</p> : null}
            {partnership.websiteUrl ? (
              <p>
                <a href={partnership.websiteUrl} target="_blank" rel="noreferrer">
                  {partnership.websiteUrl}
                </a>
              </p>
            ) : null}
            {partnership.campaign ? <p className="muted">Campaign: {partnership.campaign}</p> : null}
          </section>
        ) : null}

        {planUpgrade ? (
          <section className="partner-lead-detail__card">
            <h3>Plan upgrade</h3>
            <p>
              {planUpgrade.currentPlanName || planUpgrade.currentPlanKey} →{" "}
              {planUpgrade.targetPlanName || planUpgrade.targetPlanKey}
            </p>
            {planUpgrade.dealerCity ? <p>{planUpgrade.dealerCity}</p> : null}
            {planUpgrade.reasons?.length ? (
              <p className="muted">Reasons: {planUpgrade.reasons.join(", ")}</p>
            ) : null}
            {planUpgrade.requestStatus ? (
              <p className="muted">Request status: {planUpgrade.requestStatus}</p>
            ) : null}
          </section>
        ) : null}

        {boostRequest ? (
          <section className="partner-lead-detail__card">
            <h3>Boost request</h3>
            <p>{boostRequest.requestedPlacementType || "Featured listing"}</p>
            {boostRequest.dealerCity ? <p>{boostRequest.dealerCity}</p> : null}
            {boostRequest.requestStatus ? (
              <p className="muted">Request status: {boostRequest.requestStatus}</p>
            ) : null}
          </section>
        ) : null}

        {detail.listingTitle ? (
          <section className="partner-lead-detail__card">
            <h3>Listing context</h3>
            <p>{detail.listingTitle}</p>
          </section>
        ) : null}

        {detail.message ? (
          <section className="partner-lead-detail__card partner-lead-detail__card--wide">
            <h3>Message</h3>
            <p>{detail.message}</p>
          </section>
        ) : null}
      </div>

      <section className="partner-lead-detail__notes">
        <h3>Notes</h3>
        <label className="partner-lead-note-field">
          <span>Add note</span>
          <textarea rows={3} value={note} onChange={(event) => onNoteChange(event.target.value)} />
        </label>
        <button
          type="button"
          className="partner-dashboard-btn partner-dashboard-btn--primary"
          disabled={saving || !note.trim()}
          onClick={onSubmitNote}
        >
          Save note
        </button>

        {detail.notes?.length ? (
          <ul className="partner-lead-notes-list">
            {detail.notes.map((entry) => (
              <li key={entry.id}>
                <p>{entry.noteText}</p>
                {entry.createdAt ? (
                  <time dateTime={entry.createdAt}>{formatLeadDate(entry.createdAt)}</time>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No notes yet.</p>
        )}
      </section>
    </div>
  );
}

const PartnerLeadsPage = () => {
  const { portalLabel } = usePartnerPortalBase();
  const [leads, setLeads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const body = await listPartnerLeads({
        status: statusFilter || undefined,
      });
      setLeads(body.leads || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load leads.");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const filteredLeads = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return leads;
    return leads.filter((lead) => {
      const meta = leadMeta(lead);
      const haystack = [
        lead.lead_number,
        leadDisplayName(lead),
        lead.phone,
        lead.lead_type,
        meta?.dealerCity,
        meta?.dealerDisplayName,
        meta?.dealerBusinessName,
        meta?.targetPlanName,
        meta?.currentPlanName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [leads, search]);

  const stats = useMemo(() => {
    const newCount = leads.filter((lead) => lead.partner_status === "new").length;
    const activeCount = leads.filter((lead) =>
      ["accepted", "contacted", "appointment_set", "working"].includes(lead.partner_status)
    ).length;
    return {
      total: leads.length,
      newCount,
      activeCount,
    };
  }, [leads]);

  const openLead = async (id) => {
    setSelectedId(id);
    try {
      setDetailLoading(true);
      setErrorMessage("");
      const body = await getPartnerLead(id);
      setDetail(body.lead);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load lead detail.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const updateStatus = async (partnerStatus) => {
    if (!selectedId) return;
    try {
      setSaving(true);
      await updatePartnerLeadStatus(selectedId, { partnerStatus });
      await openLead(selectedId);
      await load();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update lead status.");
    } finally {
      setSaving(false);
    }
  };

  const submitNote = async () => {
    if (!selectedId || !note.trim()) return;
    try {
      setSaving(true);
      await addPartnerLeadNote(selectedId, note.trim());
      setNote("");
      await openLead(selectedId);
    } catch (error) {
      setErrorMessage(error.message || "Unable to save note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="partner-leads-page">
      <header className="partner-leads-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">{portalLabel}</p>
          <h2>Leads</h2>
          <p className="partner-dashboard-hero__intro">
            Review assigned opportunities, track outreach status, and capture notes for dealer
            partnerships, plan upgrades, boost requests, and buyer inquiries.
          </p>
        </div>
        <div className="partner-leads-hero__stats">
          <article className="partner-leads-stat">
            <span>Total</span>
            <strong>{stats.total}</strong>
          </article>
          <article className="partner-leads-stat partner-leads-stat--blue">
            <span>New</span>
            <strong>{stats.newCount}</strong>
          </article>
          <article className="partner-leads-stat partner-leads-stat--amber">
            <span>In progress</span>
            <strong>{stats.activeCount}</strong>
          </article>
        </div>
      </header>

      {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}

      <div className="partner-leads-layout">
        <aside className="partner-leads-sidebar partner-panel">
          <div className="partner-leads-sidebar__toolbar">
            <input
              type="search"
              className="partner-leads-search"
              placeholder="Search leads"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select
              className="partner-leads-filter"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {loading ? <p className="muted partner-leads-sidebar__empty">Loading leads…</p> : null}

          {!loading && filteredLeads.length === 0 ? (
            <div className="partner-leads-sidebar__empty">
              <p className="muted">No leads match your filters.</p>
            </div>
          ) : null}

          {!loading && filteredLeads.length > 0 ? (
            <div className="partner-leads-list">
              {filteredLeads.map((lead) => (
                <LeadListItem
                  key={lead.id}
                  lead={lead}
                  selected={selectedId === lead.id}
                  onSelect={openLead}
                />
              ))}
            </div>
          ) : null}
        </aside>

        <section className="partner-leads-main partner-panel">
          {detailLoading ? <p className="muted">Loading lead detail…</p> : null}
          {!detailLoading && detail ? (
            <LeadDetailPanel
              detail={detail}
              note={note}
              onNoteChange={setNote}
              onSubmitNote={submitNote}
              onStatusChange={updateStatus}
              saving={saving}
            />
          ) : null}
          {!detailLoading && !detail ? <LeadDetailEmpty /> : null}
        </section>
      </div>
    </div>
  );
};

export default PartnerLeadsPage;
